import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../services/api';

const Support = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: 'bug',
    title: '',
    description: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: '🐛' },
    { value: 'feature_request', label: 'Feature Request', icon: '💡' },
    { value: 'question_suggestion', label: 'Question Suggestion', icon: '❓' },
    { value: 'doubt', label: 'Doubt', icon: '🤔' },
    { value: 'general_message', label: 'General Message', icon: '💬' }
  ];

  // Cleanup audio URL on unmount or when audio changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Stop recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileChange = (e) => {
    setError('');
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return isImage || isVideo;
    });
    
    if (validFiles.length !== files.length) {
      setError('Only images and videos are allowed');
      setTimeout(() => setError(''), 3000);
    }
    
    // Limit total attachments to 5
    const totalFiles = attachments.length + validFiles.length;
    if (totalFiles > 5) {
      setError('Maximum 5 files allowed');
      setTimeout(() => setError(''), 3000);
      setAttachments(prev => [...prev, ...validFiles].slice(0, 5));
    } else {
      setAttachments(prev => [...prev, ...validFiles]);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (formData.type === 'doubt') {
      if (!formData.title.trim()) {
        setError('Please provide a title for your doubt');
        return;
      }
      if (!audioBlob && !formData.description.trim()) {
        setError('Please either record audio or write a description');
        return;
      }
    } else {
      if (!formData.title.trim()) {
        setError('Please provide a title');
        return;
      }
      if (!formData.description.trim()) {
        setError('Please provide a description');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const submitFormData = new FormData();
      
      submitFormData.append('type', String(formData.type));
      submitFormData.append('title', String(formData.title));
      submitFormData.append('description', String(formData.description || ''));

      // Append regular attachments
      attachments.forEach(file => {
        submitFormData.append('attachments', file);
      });

      // Append audio recording if exists
      if (audioBlob) {
        submitFormData.append('audioRecording', audioBlob, 'recording.webm');
      }

      const token = localStorage.getItem('token');
      
      await axios.post(
        `${BASE_URL}/api/feedback`,
        submitFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Reset form
      setFormData({ type: 'bug', title: '', description: '' });
      setAttachments([]);
      deleteAudio();
      setSuccessMessage('Your message has been submitted successfully! We\'ll review it shortly.');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(
        err.response?.data?.message || 
        'Failed to submit message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate(-1)}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back
          </button>
          <h2 style={{ color: '#667eea', fontSize: '28px', fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            💬 Support
          </h2>
          <p style={{ color: '#718096', fontSize: '15px' }}>
            Have a question, found a bug, or want to suggest a feature? We're here to help!
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #6ee7b7',
            color: '#065f46',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            ✓ {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            color: '#991b1b',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Feedback Type */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2d3748'
            }}>
              Message Type *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px'
            }}>
              {feedbackTypes.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.value })}
                  style={{
                    padding: '16px',
                    backgroundColor: formData.type === type.value ? '#667eea' : '#f7fafc',
                    color: formData.type === type.value ? '#fff' : '#4a5568',
                    border: `2px solid ${formData.type === type.value ? '#667eea' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{type.icon}</span>
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2d3748'
            }}>
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief summary of your message"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2d3748'
            }}>
              Description {formData.type === 'doubt' ? '(Optional if audio provided)' : '*'}
            </label>
            <textarea
              required={formData.type !== 'doubt'}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Provide detailed information..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Audio Recording (only for doubt type) */}
          {formData.type === 'doubt' && (
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: '#f0f4ff',
              borderRadius: '8px',
              border: '1px solid #c7d2fe'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '12px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#2d3748'
              }}>
                🎤 Record Audio (Optional)
              </label>
              
              {!audioBlob ? (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {!isRecording ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>🔴</span>
                      Start Recording
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={stopRecording}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#3b82f6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>⏹️</span>
                        Stop Recording
                      </button>
                      <span style={{
                        color: '#ef4444',
                        fontSize: '16px',
                        fontWeight: '600',
                        animation: 'pulse 1s infinite'
                      }}>
                        🔴 {formatTime(recordingTime)}
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#fff',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '500' }}>
                      🎵 Recording: {formatTime(recordingTime)}
                    </span>
                    <button
                      type="button"
                      onClick={deleteAudio}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fca5a5',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                  
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: 'none' }}
                  />
                  
                  <button
                    type="button"
                    onClick={isPlaying ? pauseAudio : playAudio}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#667eea',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {isPlaying ? '⏸️ Pause' : '▶️ Play'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* File Attachments */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2d3748'
            }}>
              Attachments (Optional)
            </label>
            <p style={{
              fontSize: '13px',
              color: '#718096',
              marginBottom: '12px'
            }}>
              Upload images or videos (max 5 files)
            </p>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            />
            
            {/* Attachment Previews */}
            {attachments.length > 0 && (
              <div style={{
                marginTop: '16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '12px'
              }}>
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      backgroundColor: '#f7fafc',
                      borderRadius: '8px',
                      padding: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{
                      fontSize: '12px',
                      color: '#4a5568',
                      marginBottom: '8px',
                      wordBreak: 'break-word'
                    }}>
                      {file.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
              style={{
                flex: 1,
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? '⏳ Submitting...' : '✉️ Submit Message'}
            </button>
          </div>
        </form>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Support;
